import { EditOutlined, UndoOutlined } from '@ant-design/icons'
import { Button, Descriptions, Flex, Result, Tag } from 'antd'
import { format, isValid, parseISO } from 'date-fns'
import { FC, ReactNode } from 'react'

interface EligibilityGateViewProps {
  computedResult: boolean | undefined
  finalResult: boolean | undefined
  isOverridden: boolean
  overrideJustification: string | undefined
  overrideActor: string | undefined
  overrideTimestamp: string | undefined
  trueLabel: string
  falseLabel: string
  incompleteLabel: string
  canEdit: boolean
  canRevert: boolean
  onEdit: () => void
  onClearOverride: () => void
}

export function formatOverrideTimestamp(
  isoTimestamp: string | undefined
): string | undefined {
  if (!isoTimestamp) return undefined

  const timestamp = parseISO(isoTimestamp)
  if (!isValid(timestamp)) {
    console.error('Invalid computed-section override timestamp:', isoTimestamp)
    return isoTimestamp
  }

  return format(timestamp, 'Pp')
}

export const EligibilityGateView: FC<EligibilityGateViewProps> = ({
  computedResult,
  finalResult,
  isOverridden,
  overrideJustification,
  overrideActor,
  overrideTimestamp,
  trueLabel,
  falseLabel,
  incompleteLabel,
  canEdit,
  canRevert,
  onEdit,
  onClearOverride,
}) => {
  const getLabel = (value: boolean | undefined) => {
    if (value === undefined) return incompleteLabel
    return value ? trueLabel : falseLabel
  }
  const formattedTimestamp = formatOverrideTimestamp(overrideTimestamp)
  const details: Array<{ key: string; label: string; children: ReactNode }> = [
    {
      key: 'system-result',
      label: 'System result',
      children: getLabel(computedResult),
    },
  ]

  if (isOverridden) {
    details.push({
      key: 'final-decision',
      label: 'Final decision',
      children: getLabel(finalResult),
    })
  }
  if (isOverridden && overrideJustification) {
    details.push({
      key: 'justification',
      label: 'Justification',
      children: overrideJustification,
    })
  }
  if (isOverridden && overrideActor) {
    details.push({
      key: 'actor',
      label: 'Recorded by',
      children: overrideActor,
    })
  }
  if (isOverridden && formattedTimestamp) {
    details.push({
      key: 'timestamp',
      label: 'Recorded at',
      children: formattedTimestamp,
    })
  }

  const actions = [
    ...(canEdit
      ? [
          <Button key='edit' icon={<EditOutlined />} onClick={onEdit}>
            {isOverridden ? 'Edit override' : 'Override decision'}
          </Button>,
        ]
      : []),
    ...(canRevert
      ? [
          <Button key='clear' icon={<UndoOutlined />} onClick={onClearOverride}>
            Revert to system result
          </Button>,
        ]
      : []),
  ]

  return (
    <Flex vertical gap='middle'>
      <Result
        status={
          finalResult === undefined
            ? 'info'
            : finalResult
              ? 'success'
              : 'warning'
        }
        title={
          <Flex justify='center' align='center' gap='small'>
            <span>{getLabel(finalResult)}</span>
            {isOverridden && <Tag color='processing'>Overridden</Tag>}
          </Flex>
        }
        extra={actions.length > 0 ? actions : undefined}
      />
      <Descriptions bordered column={1} size='small' items={details} />
    </Flex>
  )
}
